import React, { useState, useEffect } from 'react';
import TagDisplay from './TagDisplay';
import SentimentDisplay from './SentimentDisplay';
import EntityTopicDisplay from './EntityTopicDisplay';
import ClientConnections from './ClientConnections';
import ActionItems from './ActionItems';
import NotesSection from './NotesSection';
import { EditableTranscriptTitle } from './EditableTranscriptTitle';

interface Tag {
  id: string;
  name: string;
  description?: string;
  color: string;
  confidence_score?: number;
  created_by?: string;
}

interface SentimentData {
  overall_sentiment: 'positive' | 'negative' | 'neutral';
  confidence_score: number;
  positive_score: number;
  negative_score: number;
  neutral_score: number;
}

interface EmotionData {
  emotion_type: string;
  intensity: number;
  confidence: number;
  text_evidence?: string;
}

interface EntityData {
  id: string;
  entity_type: 'person' | 'place' | 'organization' | 'date' | 'event' | 'product' | 'concept';
  entity_name: string;
  entity_value: string;
  confidence: number;
  context?: string;
}

interface TopicData {
  id: string;
  topic_name: string;
  topic_category: string;
  relevance_score: number;
  confidence: number;
  keywords: string;
  summary?: string;
  context?: string;
}

interface RelationshipData {
  id: string;
  entity1_name: string;
  entity2_name: string;
  relationship_type: string;
  relationship_strength: number;
  context?: string;
  confidence: number;
}

interface Transcript {
  id: string;
  title: string;
  content: string;
  audio_metadata: {
    r2_key?: string;
    original_filename?: string;
    mime_type?: string;
    file_size_bytes?: number;
  };
  audio_duration_formatted?: string;
  created_at: string;
  updated_at: string;
}

interface TranscriptDetailProps {
  transcript: Transcript | null;
  onTitleUpdate?: (newTitle: string) => void;
  onTranscriptDelete?: (transcriptId: string) => void;
}

const TranscriptDetail: React.FC<TranscriptDetailProps> = ({ transcript, onTitleUpdate, onTranscriptDelete }) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [sentiment, setSentiment] = useState<SentimentData | null>(null);
  const [emotions, setEmotions] = useState<EmotionData[]>([]);
  const [loadingSentiment, setLoadingSentiment] = useState(false);
  const [entities, setEntities] = useState<EntityData[]>([]);
  const [topics, setTopics] = useState<TopicData[]>([]);
  const [relationships, setRelationships] = useState<RelationshipData[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(transcript?.title || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch tags and sentiment when transcript changes
  useEffect(() => {
    if (transcript) {
      // Clear previous data immediately to avoid showing stale data
      setTags([]);
      setSentiment(null);
      setEmotions([]);
      setEntities([]);
      setTopics([]);
      setRelationships([]);
      setCurrentTitle(transcript.title);
      
      // Fetch new data
      fetchTranscriptTags(transcript.id);
      fetchTranscriptSentiment(transcript.id);
      fetchTranscriptEntities(transcript.id);
    } else {
      setTags([]);
      setSentiment(null);
      setEmotions([]);
      setEntities([]);
      setTopics([]);
      setRelationships([]);
      setCurrentTitle('');
    }
  }, [transcript?.id]); // Use transcript.id instead of transcript to avoid unnecessary re-renders

  const fetchTranscriptTags = async (transcriptId: string) => {
    try {
      setLoadingTags(true);
      const response = await fetch(`/api/tags/list?transcriptId=${transcriptId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          tags?: Tag[];
          error?: string;
        };
        if (data.success) {
          setTags(data.tags || []);
        }
      }
    } catch (error) {
      console.error('Failed to fetch transcript tags:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  const fetchTranscriptSentiment = async (transcriptId: string) => {
    try {
      setLoadingSentiment(true);
      const response = await fetch(`/api/sentiment/transcript/${transcriptId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          data?: {
            sentiment: SentimentData | null;
            emotions: EmotionData[];
          };
          error?: string;
        };
        if (data.success && data.data) {
          setSentiment(data.data.sentiment);
          setEmotions(data.data.emotions || []);
        }
      }
    } catch (error) {
      console.error('Failed to fetch transcript sentiment:', error);
    } finally {
      setLoadingSentiment(false);
    }
  };

  const fetchTranscriptEntities = async (transcriptId: string) => {
    try {
      setLoadingEntities(true);
      const response = await fetch(`/api/entities/transcript/${transcriptId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          data?: {
            entities: EntityData[];
            topics: TopicData[];
            relationships: RelationshipData[];
          };
          error?: string;
        };
        if (data.success && data.data) {
          setEntities(data.data.entities || []);
          setTopics(data.data.topics || []);
          setRelationships(data.data.relationships || []);
        }
      }
    } catch (error) {
      console.error('Failed to fetch transcript entities:', error);
    } finally {
      setLoadingEntities(false);
    }
  };

  const copyTranscriptId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy transcript ID:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = id;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedId(true);
        setTimeout(() => setCopiedId(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const updateTranscriptTitle = async (newTitle: string): Promise<boolean> => {
    if (!transcript) return false;
    
    try {
      const response = await fetch(`/api/transcripts/${transcript.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title: newTitle }),
      });

      if (response.ok) {
        const data = await response.json() as { success: boolean; title?: string; error?: string };
        if (data.success) {
          setCurrentTitle(newTitle);
          // Notify parent component about the title change
          if (onTitleUpdate) {
            onTitleUpdate(newTitle);
          }
          return true;
        }
      }
      
      console.error('Failed to update transcript title');
      return false;
    } catch (error) {
      console.error('Error updating transcript title:', error);
      return false;
    }
  };

  const deleteTranscript = async () => {
    if (!transcript) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`/api/transcripts/${transcript.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json() as { success: boolean; message?: string; error?: string };
        if (data.success) {
          // Notify parent component about the deletion
          if (onTranscriptDelete) {
            onTranscriptDelete(transcript.id);
          }
          return;
        }
      }
      
      console.error('Failed to delete transcript');
      alert('Failed to delete transcript. Please try again.');
    } catch (error) {
      console.error('Error deleting transcript:', error);
      alert('Error deleting transcript. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!transcript) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <div className="text-6xl mb-4">📝</div>
          <h3 className="text-lg font-medium mb-2">No transcript selected</h3>
          <p className="text-sm">Select a transcript from the list to view its content</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return 'Unknown date';
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      {/* Header with Title and Metadata */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1 mr-4">
            <EditableTranscriptTitle
              title={currentTitle}
              transcriptId={transcript.id}
              onSave={updateTranscriptTitle}
              className="text-xl font-semibold text-gray-900"
            />
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-gray-400 font-mono">
                ID: {transcript.id}
              </span>
              <button
                onClick={() => copyTranscriptId(transcript.id)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title={copiedId ? "Copied!" : "Copy transcript ID"}
              >
                {copiedId ? (
                  <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right text-sm text-gray-500">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  📝 {transcript.content.split(/\s+/).filter(word => word.length > 0).length} words
                </span>
                <span className="flex items-center gap-1">
                  📊 {transcript.content.length.toLocaleString()} characters
                </span>
                {transcript.audio_duration_formatted && (
                  <span className="flex items-center gap-1">
                    🎵 {transcript.audio_duration_formatted}
                  </span>
                )}
              </div>
            </div>
            
            {/* Delete Button */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete transcript"
              disabled={isDeleting}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* AI Analysis - 3 Horizontal Cards */}
      <div className="bg-gray-50 border-b border-gray-200 p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
          
          {/* Tags Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                🏷️ Tags
              </h4>
              {loadingTags && <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-400"></div>}
            </div>
            {tags.length > 0 ? (
              <TagDisplay tags={tags} size="sm" showConfidence={true} />
            ) : (
              <div className="text-sm text-gray-400 italic">No tags detected</div>
            )}
          </div>

          {/* Sentiment Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                😊 Sentiment
              </h4>
              {loadingSentiment && <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-green-400"></div>}
            </div>
            {sentiment ? (
              <SentimentDisplay 
                sentiment={sentiment}
                emotions={emotions}
                size="sm"
                showDetails={true}
                transcriptId={transcript.id}
              />
            ) : (
              <div className="text-sm text-gray-400 italic">No sentiment analysis</div>
            )}
          </div>

          {/* Entities & Topics Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                🔍 Entities & Topics
              </h4>
              {loadingEntities && <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-purple-400"></div>}
            </div>
            {(entities.length > 0 || topics.length > 0) ? (
              <EntityTopicDisplay 
                entities={entities}
                topics={topics}
                relationships={relationships}
                size="sm"
                showDetails={true}
              />
            ) : (
              <div className="text-sm text-gray-400 italic">No entities detected</div>
            )}
          </div>
        </div>

        {/* Connected Clients - Full Width Horizontal Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <ClientConnections transcriptId={transcript.id} />
        </div>
      </div>

      {/* Transcript Content - Primary Focus */}
      <div className="p-6">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Transcript</h3>
          <div className="prose prose-gray max-w-none">
            <div className="text-gray-800 leading-relaxed whitespace-pre-wrap text-base">
              {transcript.content}
            </div>
          </div>
        </div>

        {/* Notes Section */}
        <div className="mt-6">
          <NotesSection transcriptId={transcript.id} />
        </div>

        {/* Action Items */}
        <div className="mt-6">
          <ActionItems transcriptId={transcript.id} />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Transcript</h3>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete "<strong>{currentTitle}</strong>"?
              </p>
              <p className="text-sm text-gray-600">
                This will permanently delete:
              </p>
              <ul className="text-sm text-gray-600 mt-2 space-y-1">
                <li>• The transcript content and audio file</li>
                <li>• All AI analysis (tags, sentiment, entities)</li>
                <li>• Notes and action items</li>
                <li>• Client connections and metrics</li>
              </ul>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={deleteTranscript}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Transcript'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptDetail; 